import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { apartmentInfo } from "../config/siteData";
import { Button } from "../components/ui/button";
import { LanguageSelector } from "./LanguageSelector";
import { useLanguage } from "../context/LanguageContext";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#galerija", label: t.nav.gallery },
    { href: "#opis", label: t.nav.about },
    { href: "#lokacija", label: t.nav.location },
    { href: "#faq", label: t.nav.faq },
    { href: "#kontakt", label: t.nav.contact },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "navbar-glass shadow-md py-3" : "bg-transparent py-4"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {/* Logo - Hidden on hero, visible when scrolled */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className={`font-bold text-xl md:text-2xl font-['Outfit'] tracking-tight transition-all duration-300 ${
              isScrolled ? "text-[#004E64] opacity-100" : "text-white opacity-0 pointer-events-none"
            }`}
            data-testid="navbar-logo"
          >
            {apartmentInfo.name}
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm font-medium transition-colors duration-300 hover:text-[#D4A373] ${
                  isScrolled ? "text-[#0F172A]" : "text-white"
                }`}
                data-testid={`nav-link-${link.href.replace("#", "")}`}
              >
                {link.label}
              </button>
            ))}
            
            {/* Language Selector */}
            <LanguageSelector isScrolled={isScrolled} />
            
            <Button
              onClick={() => scrollToSection("#upit")}
              className="btn-primary px-6 py-2 rounded-full text-sm font-medium"
              data-testid="nav-cta-button"
            >
              {t.nav.sendInquiry}
            </Button>
          </div>

          {/* Mobile: Language Selector + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector isScrolled={isScrolled} />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 transition-colors duration-300 ${
                isScrolled ? "text-[#004E64]" : "text-white"
              }`}
              aria-label="Toggle menu"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden mt-4 pb-4 animate-fadeIn"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-3 bg-white rounded-xl p-4 shadow-lg">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-[#0F172A] text-left py-2 px-3 rounded-lg hover:bg-[#f1f5f9] transition-colors duration-200"
                  data-testid={`mobile-nav-link-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection("#upit")}
                className="btn-primary w-full py-3 rounded-full text-sm font-medium mt-2"
                data-testid="mobile-nav-cta-button"
              >
                {t.nav.sendInquiry}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
